import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import LegacyFilterToolbar from "components/Common/LegacyFilterToolbar";
import { useNavigate } from "react-router-dom";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Table,
} from "reactstrap";
import { toast } from "react-toastify";

import Loaders from "components/Common/Loaders";
import ReportClientUserModal from "pages/AllCommonModal/ReportClientUserModal/report-client-user-modal";
import { AsyncClientSelect, AsyncUserSelect } from "components/Common/AsyncSearchSelects";
import {
    applyClientAsyncFilterOptionToFormState,
    applyUserAsyncFilterOptionToFormState,
    buildClientAsyncFilterInitialOption,
    buildUserAsyncFilterInitialOption,
    getClientAsyncFilterLabelFieldKey,
    getUserAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import { urlEndPoint } from "helpers/GeneralHelper";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import { usePermissionContext } from "libs/context/PermissionContext";
import {
    fetchClientUserReportMetadata,
    submitClientUserReportFilterData,
    getClientUserActivityByClientId,
} from "model/ClientUserReportModel";

const FALLBACK_TABLE_COLUMNS = [
    { Header: "SN", accessor: "sn" },
    { Header: "Client", accessor: "client" },
    { Header: "Register", accessor: "register" },
    { Header: "User Access", accessor: "userAccess", isUserAccessColumn: true },
    { Header: "", accessor: "actions", isActionsColumn: true },
];

const ACTIONS_COLUMN = {
    Header: "",
    accessor: "actions",
    isActionsColumn: true,
};

const normalizeAccessor = (value = "") =>
    String(value ?? "")
        .trim()
        .toLowerCase();

const enhanceMetadataColumns = (columns = []) => {
    const mapped = (Array.isArray(columns) ? columns : [])
        .map((col = {}) => {
            const accessor = String(col?.accessor ?? col?.field ?? "").trim();
            if (!accessor) return null;

            const normalizedAccessor = normalizeAccessor(accessor);
            const isUserAccessColumn =
                normalizedAccessor === "useraccess" ||
                normalizedAccessor === "useraccesslist";

            return {
                Header: col?.Header ?? col?.title ?? accessor,
                accessor,
                type: col?.type,
                ...(isUserAccessColumn ? { isUserAccessColumn: true } : {}),
            };
        })
        .filter(Boolean);

    const hasActionsColumn = mapped.some(
        (col) => normalizeAccessor(col?.accessor) === "actions"
    );

    if (!hasActionsColumn) {
        mapped.push(ACTIONS_COLUMN);
    } else {
        return mapped.map((col) =>
            normalizeAccessor(col?.accessor) === "actions"
                ? { ...col, isActionsColumn: true }
                : col
        );
    }

    return mapped;
};

const strOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized === "" ? null : normalized;
};

const extractFilterResponseRows = (response = {}) => {
    const responseData = response?.data ?? response ?? {};
    const nestedData =
        responseData?.data && typeof responseData.data === "object"
            ? responseData.data
            : responseData;

    if (Array.isArray(nestedData?.rows)) return nestedData.rows;
    if (Array.isArray(responseData?.rows)) return responseData.rows;
    if (Array.isArray(nestedData)) return nestedData;
    if (Array.isArray(responseData?.data)) return responseData.data;
    if (Array.isArray(responseData?.records)) return responseData.records;
    return [];
};

/** Preferred User Access chip order across all client rows. */
const USER_ACCESS_TYPE_SEQUENCE = ["account executive", "fadv send"];

const normalizeUserTypeKey = (value = "") =>
    String(value ?? "")
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, " ");

const getUserAccessTypeRank = (user = {}) => {
    const key = normalizeUserTypeKey(user?.userTypeName);
    const index = USER_ACCESS_TYPE_SEQUENCE.indexOf(key);
    return index === -1 ? USER_ACCESS_TYPE_SEQUENCE.length : index;
};

const sortUserAccessList = (users = []) =>
    [...(Array.isArray(users) ? users : [])].sort((a, b) => {
        const rankDiff = getUserAccessTypeRank(a) - getUserAccessTypeRank(b);
        if (rankDiff !== 0) return rankDiff;

        const typeCmp = normalizeUserTypeKey(a?.userTypeName).localeCompare(
            normalizeUserTypeKey(b?.userTypeName)
        );
        if (typeCmp !== 0) return typeCmp;

        return String(a?.userName ?? "")
            .trim()
            .localeCompare(String(b?.userName ?? "").trim());
    });

const mapUserAccessList = (item = {}) => {
    const raw = item?.userAccess ?? item?.users ?? item?.userAccessList ?? [];
    return sortUserAccessList(Array.isArray(raw) ? raw : []);
};

const formatUserAccessLabel = (user = {}) => {
    const userName = String(user?.userName ?? "").trim();
    const userTypeName = String(user?.userTypeName ?? "").trim();
    if (userName && userTypeName) return `${userName} (${userTypeName})`;
    return userName || userTypeName || "—";
};

const mapRowsFromApi = (rows = []) =>
    (Array.isArray(rows) ? rows : []).map((item = {}, index) => ({
        ...item,
        sn: item?.serialNumber ?? item?.sn ?? item?.sN ?? index + 1,
        client: item?.clientName ?? item?.client ?? "",
        clientId: item?.clientId ?? "",
        register:
            item?.registerDateTimeRelative ??
            item?.registerRelative ??
            item?.register ??
            "",
        registerDateTime: item?.registerDateTime ?? "",
        userAccessList: mapUserAccessList(item),
        userAccess: mapUserAccessList(item),
    }));

const extractClientUserActivityRows = (response = {}) => {
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response)) return response;

    const responseData = response?.data ?? response ?? {};
    if (Array.isArray(responseData?.data)) return responseData.data;
    if (Array.isArray(responseData?.rows)) return responseData.rows;
    if (Array.isArray(responseData?.users)) return responseData.users;
    if (Array.isArray(responseData)) return responseData;
    return [];
};

const ClientUserReport = () => {
    const navigate = useNavigate();
    const { fetchMetadataSchema } = useCommonFilterOptions() || {};
    const { permissionData } = usePermissionContext() || {};
    const canNavigateUserAccess = Boolean(
        permissionData?.UserAccess || permissionData?.UserTypeAccess
    );
    const [filterForm, setFilterForm] = useState({
        clientId: "",
        [getClientAsyncFilterLabelFieldKey()]: "",
        userId: "",
        [getUserAsyncFilterLabelFieldKey()]: "",
    });
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [metadataLoading, setMetadataLoading] = useState(true);
    const [tableMetaData, setTableMetaData] = useState([]);
    const [clientUserModalOpen, setClientUserModalOpen] = useState(false);
    const [clientUserModalLoading, setClientUserModalLoading] = useState(false);
    const [clientUserModalUsers, setClientUserModalUsers] = useState([]);

    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const tableColumns = tableMetaData?.length
        ? tableMetaData
        : FALLBACK_TABLE_COLUMNS;

    useEffect(() => {
        const bootstrap = async () => {
            setMetadataLoading(true);
            try {
                const response = await (fetchMetadataSchema
                    ? fetchMetadataSchema("metadata:clientUserReport", () =>
                          fetchClientUserReportMetadata()
                      )
                    : fetchClientUserReportMetadata());

                // Same column mapping pattern as company.jsx (title → Header, field → accessor).
                const valueReModified = response?.data?.table?.columns?.map((col) => ({
                    Header: col?.title,
                    accessor: col?.field,
                    type: col?.type,
                }));

                setTableMetaData(
                    enhanceMetadataColumns(
                        valueReModified?.length
                            ? valueReModified
                            : FALLBACK_TABLE_COLUMNS
                    )
                );
            } catch (error) {
                toast.error(error?.message || "Unable to load client user report metadata");
                setTableMetaData(enhanceMetadataColumns(FALLBACK_TABLE_COLUMNS));
            } finally {
                setMetadataLoading(false);
            }
        };

        bootstrap();
    }, [fetchMetadataSchema]);

    const buildFilterPayload = () => ({
        clientId: strOrNull(filterForm?.clientId),
        userId: strOrNull(filterForm?.userId),
    });

    const getClientUserReportList = async () => {
        setLoading(true);
        try {
            const response = await submitClientUserReportFilterData(buildFilterPayload());
            const rows = extractFilterResponseRows(response);

            if (response == null || response?.success === false) {
                setReportData([]);
                if (response?.message) {
                    toast.error(response.message);
                }
                return;
            }

            setReportData(mapRowsFromApi(rows));
        } catch (error) {
            toast.error(error?.message || "Unable to load client user report");
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleUserAccessNavigate = (user = {}) => {
        if (!canNavigateUserAccess) return;
        const userId = String(user?.userId ?? user?.id ?? "").trim();
        if (!userId) {
            toast.error("User ID is missing");
            return;
        }
        navigate(`/${urlEndPoint.manageUserAddEdit}/${encodeURIComponent(userId)}`);
    };

    const handleCloseClientUserModal = () => {
        setClientUserModalOpen(false);
        setClientUserModalUsers([]);
        setClientUserModalLoading(false);
    };

    const handleClientUserActivityClick = async (row = {}) => {
        const clientId = String(row?.clientId ?? "").trim();
        if (!clientId) {
            toast.error("Client ID is missing");
            return;
        }

        setClientUserModalUsers([]);
        setClientUserModalOpen(true);
        setClientUserModalLoading(true);

        try {
            const response = await getClientUserActivityByClientId(clientId);
            if (response == null || response?.success === false) {
                setClientUserModalUsers([]);
                if (response?.message) {
                    toast.error(response.message);
                }
                return;
            }
            setClientUserModalUsers(extractClientUserActivityRows(response));
        } catch (error) {
            toast.error(error?.message || "Unable to load client user activity");
            setClientUserModalUsers([]);
        } finally {
            setClientUserModalLoading(false);
        }
    };

    const resolveCellValue = (row = {}, column = {}) => {
        const accessor = String(column?.accessor ?? "").trim();
        if (!accessor) return undefined;

        if (row?.[accessor] !== undefined) return row[accessor];

        const normalizedAccessor = normalizeAccessor(accessor);
        if (normalizedAccessor === "sn") return row?.sn;
        if (normalizedAccessor === "client" || normalizedAccessor === "clientname") {
            return row?.client;
        }
        if (normalizedAccessor === "register") return row?.register;
        if (
            normalizedAccessor === "useraccess" ||
            normalizedAccessor === "useraccesslist"
        ) {
            return row?.userAccessList;
        }

        return row?.[accessor];
    };

    const handleSort = (column = {}) => {
        const accessor = String(column?.accessor ?? "").trim();
        if (!accessor || column?.isActionsColumn) return;

        setSortConfig((prev) => {
            if (prev.key === accessor) {
                return {
                    key: accessor,
                    direction: prev.direction === "asc" ? "desc" : "asc",
                };
            }
            return { key: accessor, direction: "asc" };
        });
    };

    const getSortComparableValue = (row = {}, accessor = "") => {
        const value = resolveCellValue(row, { accessor });
        const normalizedAccessor = normalizeAccessor(accessor);

        if (
            normalizedAccessor === "useraccess" ||
            normalizedAccessor === "useraccesslist"
        ) {
            const users = Array.isArray(row?.userAccessList)
                ? row.userAccessList
                : Array.isArray(value)
                  ? value
                  : [];
            return users.map((user) => formatUserAccessLabel(user)).join(", ");
        }

        return value;
    };

    const filteredData = useMemo(() => {
        const rows = Array.isArray(reportData) ? reportData : [];
        if (!sortConfig.key) return rows;

        return [...rows].sort((a, b) => {
            const aValue = getSortComparableValue(a, sortConfig.key);
            const bValue = getSortComparableValue(b, sortConfig.key);

            if (aValue === null || aValue === undefined || aValue === "") return 1;
            if (bValue === null || bValue === undefined || bValue === "") return -1;

            if (typeof aValue === "number" && typeof bValue === "number") {
                return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
            }

            return sortConfig.direction === "asc"
                ? String(aValue).localeCompare(String(bValue))
                : String(bValue).localeCompare(String(aValue));
        });
    }, [reportData, sortConfig]);

    const SortIcon = ({ column }) => {
        const isActive = sortConfig.key === column;
        const direction = isActive ? sortConfig.direction : null;
        const activeColor = "#212529";
        const inactiveColor = "#c4c4c4";

        return (
            <span
                style={{
                    display: "inline-flex",
                    flexDirection: "column",
                    lineHeight: 0,
                    marginLeft: 4,
                    verticalAlign: "middle",
                }}
            >
                <svg width="8" height="5" viewBox="0 0 8 5" style={{ display: "block" }}>
                    <path
                        d="M4 0l4 5H0z"
                        fill={direction === "asc" ? activeColor : inactiveColor}
                    />
                </svg>
                <svg
                    width="8"
                    height="5"
                    viewBox="0 0 8 5"
                    style={{ display: "block", marginTop: 2 }}
                >
                    <path
                        d="M4 5L0 0h8z"
                        fill={direction === "desc" ? activeColor : inactiveColor}
                    />
                </svg>
            </span>
        );
    };

    return (
        <React.Fragment>
            <div className="page-content client-user-report-page">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-3">Client User Report</h5>
                            <hr />

                            <LegacyFilterToolbar className="w-100">
                                <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
                                    <Col xs="12" md="6" lg={3}>
                                        <span className="manage-user-filter-label">Client</span>
                                        <AsyncClientSelect
                                            key={`client-user-report-client-${filterForm?.clientId || ""}`}
                                            pageName="ReportFiltered"
                                            value={filterForm?.clientId || ""}
                                            initialOption={buildClientAsyncFilterInitialOption(
                                                filterForm?.clientId,
                                                filterForm[getClientAsyncFilterLabelFieldKey()]
                                            )}
                                            onChange={(option) =>
                                                setFilterForm((previous) =>
                                                    applyClientAsyncFilterOptionToFormState(
                                                        previous,
                                                        option
                                                    )
                                                )
                                            }
                                            placeholder="Select Client"
                                        />
                                    </Col>
                                    <Col xs="12" md="6" lg={3}>
                                        <span className="manage-user-filter-label">User</span>
                                        <AsyncUserSelect
                                            key={`client-user-report-user-${filterForm?.userId || ""}`}
                                            pageName="ClientUserReport"
                                            value={filterForm?.userId || ""}
                                            initialOption={buildUserAsyncFilterInitialOption(
                                                filterForm?.userId,
                                                filterForm[getUserAsyncFilterLabelFieldKey()]
                                            )}
                                            onChange={(option) =>
                                                setFilterForm((previous) =>
                                                    applyUserAsyncFilterOptionToFormState(
                                                        previous,
                                                        option
                                                    )
                                                )
                                            }
                                            placeholder="Select User"
                                        />
                                    </Col>
                                    <Col xs="12" md="6" lg={2}>
                                        <span className="manage-user-filter-label" aria-hidden="true">
                                            &nbsp;
                                        </span>
                                        <Button
                                            color="primary"
                                            className="w-100 manage-user-search-btn"
                                            type="button"
                                            onClick={getClientUserReportList}
                                        >
                                            <i className="fa fa-search me-1" aria-hidden="true" />
                                            Search
                                        </Button>
                                    </Col>
                                </Row>
                            </LegacyFilterToolbar>

                            {metadataLoading || loading ? (
                                <Loaders />
                            ) : !tableColumns?.length ? (
                                <div className="text-muted text-center py-5">
                                    Unable to load table columns.
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table
                                        className="align-middle table-nowrap mb-0 client-user-report-table"
                                        hover
                                    >
                                        <thead className="table-light">
                                            <tr>
                                                {tableColumns.map((column) => (
                                                    <th
                                                        key={column.accessor}
                                                        onClick={() => handleSort(column)}
                                                        style={
                                                            column?.isActionsColumn
                                                                ? undefined
                                                                : { cursor: "pointer" }
                                                        }
                                                    >
                                                        {column.Header}
                                                        {!column?.isActionsColumn ? (
                                                            <SortIcon column={column.accessor} />
                                                        ) : null}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredData.length ? (
                                                filteredData.map((row, rowIndex) => {
                                                    const rowKey =
                                                        row?.clientId ||
                                                        `client-user-row-${rowIndex}`;

                                                    return (
                                                        <tr key={rowKey}>
                                                            {tableColumns.map((column) => {
                                                                const value = resolveCellValue(
                                                                    row,
                                                                    column
                                                                );

                                                                if (column.isUserAccessColumn) {
                                                                    const users = Array.isArray(
                                                                        row?.userAccessList
                                                                    )
                                                                        ? row.userAccessList
                                                                        : Array.isArray(value)
                                                                          ? value
                                                                          : [];

                                                                    return (
                                                                        <td key={column.accessor}>
                                                                            {users.length ? (
                                                                                <div className="client-user-report-access-list">
                                                                                    {users.map(
                                                                                        (user, userIndex) => {
                                                                                            const userKey =
                                                                                                user?.userId ||
                                                                                                `user-${userIndex}`;
                                                                                            const label =
                                                                                                formatUserAccessLabel(
                                                                                                    user
                                                                                                );

                                                                                            if (!canNavigateUserAccess) {
                                                                                                return (
                                                                                                    <span
                                                                                                        key={userKey}
                                                                                                        className="client-user-report-access-chip client-user-report-access-chip--static"
                                                                                                    >
                                                                                                        {label}
                                                                                                    </span>
                                                                                                );
                                                                                            }

                                                                                            return (
                                                                                                <button
                                                                                                    key={userKey}
                                                                                                    type="button"
                                                                                                    className="client-user-report-access-chip"
                                                                                                    onClick={() =>
                                                                                                        handleUserAccessNavigate(
                                                                                                            user
                                                                                                        )
                                                                                                    }
                                                                                                >
                                                                                                    {label}
                                                                                                </button>
                                                                                            );
                                                                                        }
                                                                                    )}
                                                                                </div>
                                                                            ) : (
                                                                                "—"
                                                                            )}
                                                                        </td>
                                                                    );
                                                                }

                                                                if (column.isActionsColumn) {
                                                                    return (
                                                                        <td
                                                                            key={column.accessor}
                                                                            className="text-end"
                                                                        >
                                                                            <button
                                                                                type="button"
                                                                                className="client-user-report-info-btn"
                                                                                aria-label="View client user activity"
                                                                                onClick={() =>
                                                                                    handleClientUserActivityClick(
                                                                                        row
                                                                                    )
                                                                                }
                                                                            >
                                                                                <i
                                                                                    className="mdi mdi-information-outline"
                                                                                    aria-hidden
                                                                                />
                                                                            </button>
                                                                        </td>
                                                                    );
                                                                }

                                                                return (
                                                                    <td key={column.accessor}>
                                                                        {value === null ||
                                                                        value === undefined ||
                                                                        value === ""
                                                                            ? "—"
                                                                            : value}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td
                                                        colSpan={tableColumns.length}
                                                        className="text-center text-muted py-4"
                                                    >
                                                        No records found.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Container>

                <ReportClientUserModal
                    isOpen={clientUserModalOpen}
                    onClose={handleCloseClientUserModal}
                    users={clientUserModalUsers}
                    loading={clientUserModalLoading}
                />
            </div>
        </React.Fragment>
    );
};

ClientUserReport.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(ClientUserReport);
